import { useState, useEffect, useRef, useCallback } from "react";
import { API_GRAPHQL } from "../../../../constants";
import { axiosAuthFetch } from "../../../../helpers/axios";
import useDebounce from "../../../useDebounce";

// Antall måltider per henting. Holdes lavt med vilje, så velgeren åpner raskt.
export const MEAL_PAGE_SIZE = 50;

const MEAL_FIELDS = `
    id
    attributes{
        name
        category
        kcals
        proteins
        carbs
        fats
        meal_images{
            data{
                id
                attributes{
                    image{
                        data{
                            id
                            attributes{
                                url
                            }
                        }
                    }
                }
            }
        }
        meal_instances(pagination: {limit: 50}){
            data{
                id
                attributes{
                    variant_name
                    proteins
                    fats
                    carbs
                    kcals
                }
            }
        }
    }
`;

// Id-er settes rett inn i spørringen, så de må være rene heltall
function toId(value, label) {
    const id = Number(value);
    if (value === null || value === '' || !Number.isInteger(id)) {
        throw new Error(`Ugyldig ${label}: ${value}`);
    }
    return id;
}

function readMeals(response) {
    const meals = response?.data?.meals;
    if (!Array.isArray(meals?.data)) {
        throw new Error('Uventet svar fra serveren ved henting av måltider');
    }
    return meals;
}

// Henter én side av coachens måltider, nyeste først. Søket gjøres på serveren
// (containsi på navn) over ALLE måltidene til coachen, ikke bare de som alt er
// lastet. Samme coach-filter som måltidsbiblioteket under «Måltider».
export async function GetCoachMealsPage({ coachId, search = '', start = 0, limit = MEAL_PAGE_SIZE }) {
    const id = toId(coachId, 'coach-id');
    const term = search.trim();

    const query = `
        query${term ? '($term: String)' : ''} {
            meals(
                filters: { ${term ? 'name: { containsi: $term },' : ''} coaches: { id: { eq: ${id} } } }
                sort: "id:desc"
                pagination: { start: ${toId(start, 'start')}, limit: ${toId(limit, 'limit')} }
            ){
                meta{
                    pagination{
                        total
                    }
                }
                data{
                    ${MEAL_FIELDS}
                }
            }
        }
    `;

    const body = term ? { query, variables: { term } } : { query };
    const meals = readMeals(await axiosAuthFetch(API_GRAPHQL, body, 'post'));
    const total = meals.meta?.pagination?.total;

    return { meals: meals.data, total: typeof total === 'number' ? total : null };
}

const SELECTION_CHUNK = 50;

// Henter måltidene som allerede er valgt i en matplan (direkte, eller via en
// valgt variant), uavhengig av hvor i biblioteket de ligger. Uten dette ville
// valgte måltider utenfor første side verken vises eller telle med i makroene.
export async function GetCoachMealsForSelection({ coachId, mealIds = [], instanceIds = [] }) {
    const id = toId(coachId, 'coach-id');
    const wanted = [
        ...[...new Set(mealIds.map((mealId) => toId(mealId, 'måltid-id')))].map((value) => ({ type: 'meal', value })),
        ...[...new Set(instanceIds.map((instanceId) => toId(instanceId, 'variant-id')))].map((value) => ({ type: 'instance', value })),
    ];

    const chunks = [];
    for (let i = 0; i < wanted.length; i += SELECTION_CHUNK) chunks.push(wanted.slice(i, i + SELECTION_CHUNK));

    const results = await Promise.all(chunks.map(async (chunk) => {
        const meals = chunk.filter((item) => item.type === 'meal').map((item) => item.value);
        const instances = chunk.filter((item) => item.type === 'instance').map((item) => item.value);
        const either = [
            meals.length ? `{ id: { in: [${meals.join(', ')}] } }` : '',
            instances.length ? `{ meal_instances: { id: { in: [${instances.join(', ')}] } } }` : '',
        ].filter(Boolean).join(', ');

        const query = `
            query {
                meals(
                    filters: { coaches: { id: { eq: ${id} } }, or: [${either}] }
                    pagination: { limit: ${chunk.length} }
                ){
                    data{
                        ${MEAL_FIELDS}
                    }
                }
            }
        `;
        return readMeals(await axiosAuthFetch(API_GRAPHQL, { query }, 'post')).data;
    }));

    const byId = new Map();
    results.flat().forEach((meal) => byId.set(String(meal.id), meal));
    return [...byId.values()];
}

const normalize = (term) => (term || '').trim().toLowerCase();

const NOT_LOADED = { ids: [], fetched: 0, hasMore: true, isLoading: false, loaded: false };

function isSelectedInSlot(meal, slot) {
    return (slot?.selectedMealsAndInstances || []).some((item) =>
        (item.type === 'meal' && String(item.id) === String(meal.id)) ||
        (item.type === 'instance' &&
            meal.attributes.meal_instances?.data?.some((inst) => String(inst.id) === String(item.id)))
    );
}

// Måltidsvelgeren i matplan-skjemaene. Laster første side ved åpning, søker på
// serveren (debouncet) for hvert søkeord som står i et måltidsfelt, og henter
// flere sider på forespørsel. initialSelection er valgene planen har fra før.
export function useCoachMealPicker(coachId, searchTerms = [], initialSelection = []) {
    const [mealsById, setMealsById] = useState({});
    const [lists, setLists] = useState({});
    // Om måltidene som allerede ligger i planen er hentet: 'loading' | 'ready' | 'error'
    const [selectionStatus, setSelectionStatus] = useState('loading');

    const progress = useRef({});
    const inFlight = useRef(new Set());
    // Økes når velgeren nullstilles eller lukkes, så sene svar fra før ikke blandes inn
    const generation = useRef(0);
    const initialSelectionRef = useRef(initialSelection);

    const addMeals = useCallback((meals) => {
        setMealsById((prev) => {
            const next = { ...prev };
            meals.forEach((meal) => { next[meal.id] = meal; });
            return next;
        });
    }, []);

    const loadPage = useCallback(async (rawTerm) => {
        const term = normalize(rawTerm);
        const key = `${coachId}|${term}`;
        const current = progress.current[key] || { fetched: 0, hasMore: true };
        if (inFlight.current.has(key) || !current.hasMore) return;

        const startedIn = generation.current;
        inFlight.current.add(key);
        setLists((prev) => ({ ...prev, [term]: { ...(prev[term] || NOT_LOADED), isLoading: true } }));
        try {
            const { meals, total } = await GetCoachMealsPage({ coachId, search: term, start: current.fetched });
            if (startedIn !== generation.current) return;
            const fetched = current.fetched + meals.length;
            // Stoler på total fra serveren når den finnes; ellers antar vi at en full side betyr at det er mer
            const hasMore = meals.length > 0 && (total !== null ? fetched < total : meals.length === MEAL_PAGE_SIZE);
            progress.current[key] = { fetched, hasMore };

            addMeals(meals);
            setLists((prev) => {
                const ids = [...(prev[term]?.ids || [])];
                meals.forEach((meal) => { if (!ids.includes(meal.id)) ids.push(meal.id); });
                return { ...prev, [term]: { ids, fetched, hasMore, isLoading: false, loaded: true } };
            });
        } catch (error) {
            console.error('Feil ved henting av måltider:', error);
            if (startedIn !== generation.current) return;
            setLists((prev) => ({ ...prev, [term]: { ...(prev[term] || NOT_LOADED), isLoading: false } }));
        } finally {
            if (startedIn === generation.current) inFlight.current.delete(key);
        }
    }, [coachId, addMeals]);

    // Ved åpning: første side av biblioteket, pluss måltidene planen allerede inneholder
    useEffect(() => {
        let isActive = true;
        generation.current += 1;
        inFlight.current = new Set();
        progress.current = {};
        setLists({});
        setMealsById({});
        setSelectionStatus('loading');
        loadPage('');

        async function loadSelection() {
            const selection = initialSelectionRef.current || [];
            const mealIds = selection.filter((item) => item.type === 'meal').map((item) => item.id);
            const instanceIds = selection.filter((item) => item.type === 'instance').map((item) => item.id);
            try {
                if (mealIds.length || instanceIds.length) {
                    const meals = await GetCoachMealsForSelection({ coachId, mealIds, instanceIds });
                    if (!isActive) return;
                    addMeals(meals);
                }
                if (isActive) setSelectionStatus('ready');
            } catch (error) {
                console.error('Feil ved henting av måltidene i planen:', error);
                if (isActive) setSelectionStatus('error');
            }
        }
        loadSelection();

        return () => {
            isActive = false;
            generation.current += 1;
        };
    }, [coachId, loadPage, addMeals]);

    // Søk på serveren når brukeren har stoppet å skrive
    const termsKey = JSON.stringify([...new Set(searchTerms.map(normalize))].filter(Boolean).sort());
    const debouncedTermsKey = useDebounce(termsKey, 300);
    useEffect(() => {
        JSON.parse(debouncedTermsKey).forEach((term) => {
            if (!progress.current[`${coachId}|${term}`]) loadPage(term);
        });
    }, [debouncedTermsKey, coachId, loadPage]);

    // Måltidene som skal vises i velgeren for et måltidsfelt (slot) med gitt søkeord
    const mealsFor = (rawTerm, slot) => {
        const term = normalize(rawTerm);
        const list = lists[term];
        const matches = (meal) => (meal.attributes.name || '').toLowerCase().includes(term);

        // Mens søket går viser vi treffene blant det som alt er lastet
        if (!list?.loaded) {
            return Object.values(mealsById).reverse().filter(matches);
        }

        // Valgte måltider som ligger utenfor de lastede sidene vises først, så de alltid er synlige
        const selectedOutside = Object.values(mealsById)
            .reverse()
            .filter((meal) => !list.ids.includes(meal.id) && matches(meal) && isSelectedInSlot(meal, slot));
        return [...selectedOutside, ...list.ids.map((mealId) => mealsById[mealId]).filter(Boolean)];
    };

    const stateFor = (rawTerm) => lists[normalize(rawTerm)] || NOT_LOADED;

    return {
        knownMeals: Object.values(mealsById),
        mealsFor,
        hasMore: (term) => stateFor(term).hasMore,
        isLoading: (term) => stateFor(term).isLoading,
        loadMore: loadPage,
        selectionStatus,
    };
}
